import Phaser from 'phaser';
import { TILE_SIZE } from '@shared/constants';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getCraftingStationMeta } from '@shared/data/craftingStations';

/** Estado sincronizado mínimo de una estación (lo que necesita el render). */
export interface CraftingStationStateView {
  id: string;
  stationType: string;
  x: number;
  y: number;
}

interface DepthScene {
  getEntityBaseDepthFromTileY(tileY: number): number;
}

/**
 * Renderer de ESTACIONES DE CRAFTEO (bancos/objetos del mundo). Placeholder hasta tener arte:
 * dibuja un marcador (rect + etiqueta con el nombre de la estación) en el tile, clickeable →
 * abre la ventana de crafteo (la proximidad ≤2 tiles la valida GameScene, que conoce la
 * posición del player). Calca el patrón de ResourceNodeRenderer. Ver docs/CRAFTING_SYSTEM.md.
 */
export class CraftingStationRenderer {
  private visuals = new Map<string, Phaser.GameObjects.Container>();

  constructor(
    private scene: Phaser.Scene,
    private onClick: (stationId: string, stationType: string, tileX: number, tileY: number) => void
  ) {}

  private depthFor(tileY: number): number {
    const s = this.scene as unknown as Partial<DepthScene>;
    return typeof s.getEntityBaseDepthFromTileY === 'function' ? s.getEntityBaseDepthFromTileY(tileY) : tileY;
  }

  public addOrUpdate(id: string, st: CraftingStationStateView): void {
    const worldX = st.x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = st.y * TILE_SIZE + TILE_SIZE / 2;
    let container = this.visuals.get(id);
    if (!container) {
      container = this.scene.add.container(worldX, worldY);
      container.setDepth(this.depthFor(st.y));
      // Marcador placeholder: base de madera + techo, hasta tener sprite propio.
      const g = this.scene.add.graphics();
      g.fillStyle(0x6b4a2a, 1);
      g.fillRoundedRect(-14, -10, 28, 20, 4);
      g.lineStyle(2, 0x3f2a16, 1);
      g.strokeRoundedRect(-14, -10, 28, 20, 4);
      g.fillStyle(0x8a5a30, 1);
      g.fillRect(-16, -14, 32, 6);
      container.add(g);
      const meta = getCraftingStationMeta(st.stationType);
      const label = this.scene.add
        .text(0, -22, meta?.name ?? 'Estación', {
          fontFamily: GAME_FONT_FAMILY,
          fontSize: '11px',
          color: '#e8c98a',
          resolution: 2,
        })
        .setOrigin(0.5, 1);
      container.add(label);
      container.setSize(TILE_SIZE, TILE_SIZE);
      container.setInteractive(
        new Phaser.Geom.Rectangle(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE),
        Phaser.Geom.Rectangle.Contains
      );
      container.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
      container.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
      container.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev?: Phaser.Types.Input.EventData) => {
        ev?.stopPropagation?.();
        this.onClick(id, st.stationType, st.x, st.y);
      });
      this.visuals.set(id, container);
    } else {
      container.setPosition(worldX, worldY);
      container.setDepth(this.depthFor(st.y));
    }
  }

  public remove(id: string): void {
    this.visuals.get(id)?.destroy();
    this.visuals.delete(id);
  }
}
